import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  TrendingUp,
  Activity,
  BarChart3,
  FileText,
  Package,
  Receipt,
  Megaphone,
  Briefcase,
  Settings,
  Mic,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Network,
  Upload,
  Bell,
  GitBranch,
  DatabaseZap,
  DollarSign,
  List,
  Shield,
  Copy,
  Inbox,
} from 'lucide-react';
import { logout } from '../../services/api';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/contacts', icon: Users, label: 'Contacts' },
  { to: '/organizations', icon: Building2, label: 'Organizations' },
  { to: '/pipeline', icon: TrendingUp, label: 'Pipeline' },
  { to: '/campaigns', icon: Megaphone, label: 'Campaigns' },
  { to: '/network-insights', icon: Network, label: 'Network Insights' },
  { to: '/reminders', icon: Bell, label: 'Reminders' },
  { to: '/attribution', icon: GitBranch, label: 'Attribution' },
  { to: '/enrichment', icon: DatabaseZap, label: 'Enrichment' },
  { to: '/revenue', icon: DollarSign, label: 'Revenue' },
  { to: '/smart-lists', icon: List, label: 'Smart Lists' },
  { to: '/intelligence', icon: Shield, label: 'Intelligence' },
  { to: '/dedup', icon: Copy, label: 'Dedup' },
  { to: '/job-search', icon: Briefcase, label: 'Job Search' },
  { to: '/freelancer', icon: Inbox, label: 'Freelancer' },
  { to: '/activities', icon: Activity, label: 'Activities' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/documents', icon: FileText, label: 'Documents' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/billing', icon: Receipt, label: 'Billing' },
];

const EXPANDED_W = 240;
const COLLAPSED_W = 80;

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const w = collapsed ? COLLAPSED_W : EXPANDED_W;

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className="fixed left-0 top-0 bottom-0 flex flex-col z-30"
        style={{
          width: w,
          background: '#0a0a0a',
          borderRight: '1px solid var(--border-primary)',
          transition: 'width 0.2s ease',
          overflow: 'hidden',
        }}
      >
        {/* Logo + collapse toggle */}
        <div className="px-4 py-5 flex items-center justify-between" style={{ minHeight: 64 }}>
          {!collapsed && (
            <div>
              <h1 className="font-display text-2xl" style={{ color: 'var(--netrun-green)' }}>KOG</h1>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Sales & Service Pipeline</p>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg hover:bg-white/5"
            style={{ color: 'var(--text-muted)' }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg text-sm transition-all duration-150 ${
                  collapsed ? 'justify-center px-2 py-3.5' : 'px-3 py-2.5'
                } ${isActive ? '' : 'hover:bg-white/5'}`
              }
              style={({ isActive }) => ({
                background: isActive ? 'var(--netrun-green)' : 'transparent',
                color: isActive ? '#000' : 'var(--text-secondary)',
                ...(isActive && collapsed
                  ? { borderLeft: '3px solid var(--netrun-green)', borderRadius: '0 8px 8px 0', background: 'rgba(144, 185, 171, 0.15)' }
                  : {}),
              })}
            >
              <Icon size={collapsed ? 28 : 18} style={{ flexShrink: 0 }} />
              {!collapsed && <span className="font-body whitespace-nowrap">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="px-2 pb-3 space-y-0.5">
          <NavLink
            to="/voice"
            title={collapsed ? 'Charlotte Voice' : undefined}
            className={`flex items-center gap-3 rounded-lg text-sm transition-all duration-150 ${
              collapsed ? 'justify-center px-2 py-3.5' : 'px-3 py-2.5'
            }`}
            style={({ isActive }) => ({
              background: isActive ? 'rgba(144, 185, 171, 0.15)' : 'transparent',
              color: isActive ? 'var(--netrun-green)' : 'var(--text-secondary)',
              border: '1px solid',
              borderColor: isActive ? 'var(--netrun-green)' : 'var(--border-subtle)',
            })}
          >
            <Mic size={collapsed ? 28 : 18} style={{ flexShrink: 0 }} />
            {!collapsed && <span className="font-body">Charlotte Voice</span>}
          </NavLink>

          <NavLink
            to="/linkedin-import"
            title={collapsed ? 'LinkedIn Import' : undefined}
            className={`flex items-center gap-3 rounded-lg text-sm ${
              collapsed ? 'justify-center px-2 py-3.5' : 'px-3 py-2.5'
            }`}
            style={({ isActive }) => ({
              background: isActive ? 'rgba(144, 185, 171, 0.15)' : 'transparent',
              color: isActive ? 'var(--netrun-green)' : 'var(--text-muted)',
            })}
          >
            <Upload size={collapsed ? 28 : 18} style={{ flexShrink: 0 }} />
            {!collapsed && <span className="font-body">LinkedIn Import</span>}
          </NavLink>

          <NavLink
            to="/settings"
            title={collapsed ? 'Settings' : undefined}
            className={`flex items-center gap-3 rounded-lg text-sm ${
              collapsed ? 'justify-center px-2 py-3.5' : 'px-3 py-2.5'
            }`}
            style={{ color: 'var(--text-muted)' }}
          >
            <Settings size={collapsed ? 28 : 18} style={{ flexShrink: 0 }} />
            {!collapsed && <span className="font-body">Settings</span>}
          </NavLink>

          <button
            onClick={handleLogout}
            title={collapsed ? 'Sign Out' : undefined}
            className={`flex items-center gap-3 rounded-lg text-sm w-full cursor-pointer ${
              collapsed ? 'justify-center px-2 py-3.5' : 'px-3 py-2.5'
            }`}
            style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none' }}
          >
            <LogOut size={collapsed ? 28 : 18} style={{ flexShrink: 0 }} />
            {!collapsed && <span className="font-body">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Spacer that pushes main content right — matches sidebar width */}
      <div style={{ width: w, flexShrink: 0, transition: 'width 0.2s ease' }} />
    </>
  );
}
